/* istanbul ignore file */
import { useState, useRef, useEffect } from 'react'
import configment from '@adc/configment'
import { getListingImageUrl } from '../../utils/listing'
import useMountEffect from '../useMountEffect'

// TODO: Convert this to an object param to avoid passing so many params
export const useBackgroundImageWithFallback = (
  imageUrl,
  showEnhanced,
  alreadyVerifiedPhoto = null,
  onUnmount = () => {},
  defaultPhotoConfigKey = 'LISTING_DEFAULT_PHOTO_URL'
) => {
  // we allow to bypass the image url and use an already verified image
  if (alreadyVerifiedPhoto) {
    return alreadyVerifiedPhoto
  }
  const defaultPhotoUrl = configment.get(defaultPhotoConfigKey, '')

  const targetPhotoUrl =
    imageUrl !== defaultPhotoUrl
      ? getListingImageUrl(imageUrl, showEnhanced?.enabled, defaultPhotoUrl)
      : imageUrl
  // Check if running in a non-browser environment
  if (typeof window === 'undefined' || typeof Image === 'undefined') {
    return targetPhotoUrl
  }
  const helperImg = useRef(null)
  const fallbackPhotoUrl = imageUrl

  const [backgroundImageUrl, setBackgroundImageUrl] = useState(
    targetPhotoUrl || defaultPhotoUrl
  )

  const updateImage = imageUrl => {
    setBackgroundImageUrl(imageUrl)
    cleanRef()
  }

  const cleanRef = () => {
    if (helperImg.current) {
      helperImg.current.onload = null
      helperImg.current.onerror = null
      helperImg.current = null
    }
  }

  useEffect(() => {
    if (
      imageUrl &&
      backgroundImageUrl &&
      imageUrl !== backgroundImageUrl &&
      backgroundImageUrl !== getListingImageUrl(imageUrl, true, defaultPhotoUrl)
    ) {
      setBackgroundImageUrl(imageUrl)
    } else if (!imageUrl) {
      setBackgroundImageUrl(defaultPhotoUrl)
    }
  }, [imageUrl, backgroundImageUrl, defaultPhotoUrl])

  // this function attempts to replicate the behavior of the ListingImage component without needing to use an <img /> tag
  const getValidBackgroundImageUrl = (imageUrl, fallbackPhotoUrl = null) => {
    const testFallbackImage = () => {
      if (fallbackPhotoUrl?.length) {
        return getValidBackgroundImageUrl(fallbackPhotoUrl)
      }
      updateImage(defaultPhotoUrl)
    }
    if (imageUrl?.length) {
      if (imageUrl === defaultPhotoUrl) {
        updateImage(defaultPhotoUrl)
        return
      }
      const img = new Image()
      img.onload = () => updateImage(imageUrl)
      img.onerror = testFallbackImage
      img.src = imageUrl
      helperImg.current = img
    } else {
      testFallbackImage()
    }
  }

  useMountEffect(() => {
    getValidBackgroundImageUrl(targetPhotoUrl, fallbackPhotoUrl)

    // Cleanup function to avoid setting state on unmounted component
    // useful especially if the parent component unmounted during verification
    // we include an optional onUnmount callback that will help with virtual
    // scrolling card list in SRP
    return () => {
      if (helperImg.current) {
        onUnmount()
        cleanRef()
      }
    }
  })

  return backgroundImageUrl
}

export default useBackgroundImageWithFallback
